import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Button,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  Link,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Text,
  Toast,
  ToastTitle,
  tokens,
  useToastController,
} from "@fluentui/react-components";
import {
  AddCircle20Regular,
  ArrowUpload20Regular,
  Clock20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { ApiError } from "@/lib/apiClient";
import { getParcel } from "@/api/parcels";
import { lookupMotCandidates } from "@/api/documents";
import { pushToSymphony } from "@/api/symphony";
import { createVessel } from "@/api/vessels";
import { ParcelPreviewModal } from "@/features/review/ParcelPreviewModal";
import { formatWithThousands, parseNumeric } from "@/lib/formatNumber";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type {
  DocumentDetail,
  FieldSpec,
  MotCandidate,
  SymphonyPushError,
} from "@/types/domain";

interface SymphonyUpdatePanelProps {
  detail: DocumentDetail;
  toasterId: string;
}

type FieldChoiceState = number | "manual";
interface FieldEntry {
  /** The current text/number/boolean value (used for kind="value" and choice→manual fallback). */
  value: string;
  /** Index into spec.choices, or "manual" for free-text fallback. */
  choiceState: FieldChoiceState;
}

function valueToInputString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/**
 * Normalize a backend default for a `datetime`-typed value field into the
 * `YYYY-MM-DDTHH:mm` shape that `<input type="datetime-local">` accepts.
 *
 * The extractor emits dates as bare ISO date strings (`"2025-11-04"`) for
 * fields like `bl_date`, and Pydantic-serialized datetimes can include a
 * `Z` / `±HH:MM` timezone suffix — both are silently rejected by the
 * input, leaving the field empty.
 */
function toDatetimeLocalInputString(v: unknown): string {
  if (v === null || v === undefined) return "";
  const raw = String(v).trim();
  if (raw === "") return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00`;
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?/);
  if (m) return `${m[1]}T${m[2]}`;
  const parsed = Date.parse(raw);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return raw;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const CUSTOM_OPTION_KEY = "__custom__";

const DATETIME_FMT = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * Re-format a Symphony Choice's label to be human-readable.
 *
 * - For ``datetime`` fields the backend currently sends the raw ISO
 *   string (sometimes followed by " — description"). Reformat the
 *   leading ISO into a short locale datetime. The trailing description
 *   is surfaced separately via ``choiceSubtitle`` and is NOT included
 *   in the primary label — that keeps the collapsed-dropdown display
 *   clean (datetime only) while still letting the expanded list and
 *   tooltips show the source description.
 * - For non-datetime choices the existing label is already meaningful
 *   (e.g. ``31995.85 MT (mt_air)``) — leave it alone.
 */
function splitChoiceLabel(rawLabel: string): { iso: string; trailing: string | null } {
  const sepMatch = rawLabel.match(/^([^—]+?)\s+—\s+(.*)$/);
  if (!sepMatch) return { iso: rawLabel, trailing: null };
  const iso = sepMatch[1] ?? rawLabel;
  const trailing = sepMatch[2] ?? null;
  return { iso, trailing };
}

function formatChoiceLabel(spec: FieldSpec, rawLabel: string): string {
  if (spec.type === "datetime") {
    const { iso } = splitChoiceLabel(rawLabel);
    const parsed = Date.parse(iso.trim());
    if (Number.isNaN(parsed)) return rawLabel;
    return DATETIME_FMT.format(new Date(parsed));
  }
  if (spec.type === "number") {
    // Choice labels for number specs come from the backend as
    // ``"31995.85 BBL (gross_quantity_bbls)"`` (or ``"31995.85
    // (gross_quantity_bbls)"`` when the UOM is filtered out). Reformat
    // the leading numeric token with locale thousands separators so the
    // dropdown matches how quantities render elsewhere in the tab.
    return rawLabel.replace(/^(-?\d+(?:\.\d+)?)/, (m) => formatWithThousands(m));
  }
  return rawLabel;
}

function choiceSubtitle(spec: FieldSpec, rawLabel: string): string | null {
  if (spec.type !== "datetime") return null;
  const { trailing } = splitChoiceLabel(rawLabel);
  return trailing !== null ? trailing.trim() : null;
}

function defaultEntryFor(spec: FieldSpec): FieldEntry {
  if (spec.kind === "choice") {
    const choices = spec.choices ?? [];
    if (choices.length === 0) {
      // No candidates → free-text fallback.
      return {
        value:
          spec.type === "datetime"
            ? toDatetimeLocalInputString(spec.default)
            : valueToInputString(spec.default),
        choiceState: "manual",
      };
    }
    const idx =
      spec.defaultChoiceIndex !== null && spec.defaultChoiceIndex !== undefined
        ? spec.defaultChoiceIndex
        : 0;
    return {
      value:
        spec.type === "datetime"
          ? toDatetimeLocalInputString(choices[idx]?.value)
          : valueToInputString(choices[idx]?.value),
      choiceState: idx,
    };
  }
  // kind === "value"
  if (spec.type === "boolean") {
    return {
      value:
        typeof spec.default === "boolean"
          ? String(spec.default)
          : "false",
      choiceState: "manual",
    };
  }
  if (spec.type === "datetime") {
    return {
      value: toDatetimeLocalInputString(spec.default),
      choiceState: "manual",
    };
  }
  return {
    value: valueToInputString(spec.default),
    choiceState: "manual",
  };
}

/** Coerce the raw string back to the typed value the API expects. */
function coerceValue(spec: FieldSpec, entry: FieldEntry): unknown {
  if (spec.kind === "choice" && entry.choiceState !== "manual") {
    const choice = spec.choices?.[entry.choiceState];
    return choice?.value ?? null;
  }
  const raw = entry.value.trim();
  if (raw === "") return null;
  if (spec.type === "number") {
    const n = Number(raw.replace(/,/g, ""));
    return Number.isNaN(n) ? raw : n;
  }
  if (spec.type === "boolean") {
    return raw === "true" || raw === "1" || raw.toLowerCase() === "yes";
  }
  return raw;
}

function inputTypeFor(spec: FieldSpec): "text" | "number" | "datetime-local" {
  if (spec.type === "number") return "number";
  if (spec.type === "datetime") return "datetime-local";
  return "text";
}

function isFieldFilled(spec: FieldSpec, entry: FieldEntry): boolean {
  if (spec.kind === "choice" && entry.choiceState !== "manual") {
    return spec.choices?.[entry.choiceState] !== undefined;
  }
  return entry.value.trim() !== "";
}

export function SymphonyUpdatePanel({
  detail,
  toasterId,
}: SymphonyUpdatePanelProps) {
  const qc = useQueryClient();
  const { dispatchToast } = useToastController(toasterId);
  const fieldSet = detail.symphonyFieldSet;
  const editableFields = fieldSet.editableFields;

  const [entries, setEntries] = useState<Record<string, FieldEntry>>(() => {
    const next: Record<string, FieldEntry> = {};
    for (const spec of editableFields) {
      next[spec.name] = defaultEntryFor(spec);
    }
    return next;
  });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [pushError, setPushError] = useState<SymphonyPushError | null>(null);
  const [selectedActualNumber, setSelectedActualNumber] = useState<number | null>(null);

  const requiresActualPicker = fieldSet.requiresActualPicker;
  const parcelQuery = useQuery({
    queryKey: ["parcel", detail.selectedParcelNumber],
    queryFn: () => getParcel(detail.selectedParcelNumber as number),
    enabled: requiresActualPicker && detail.selectedParcelNumber !== null,
  });

  const actualOptions = useMemo<ActualOption[]>(() => {
    if (!parcelQuery.data) return [];
    return extractActualOptions(parcelQuery.data);
  }, [parcelQuery.data]);

  // Auto-pick when there is exactly one actual; reset when the parcel changes
  // or the picker becomes irrelevant (non-cert doc type).
  useEffect(() => {
    if (!requiresActualPicker) {
      setSelectedActualNumber(null);
      return;
    }
    if (actualOptions.length === 1) {
      setSelectedActualNumber(actualOptions[0]!.actualNumber);
    } else if (
      selectedActualNumber !== null &&
      !actualOptions.some((a) => a.actualNumber === selectedActualNumber)
    ) {
      setSelectedActualNumber(null);
    }
  }, [requiresActualPicker, actualOptions, selectedActualNumber]);

  const setEntry = (name: string, partial: Partial<FieldEntry>): void => {
    setEntries((prev) => {
      const current = prev[name] ?? { value: "", choiceState: "manual" };
      return { ...prev, [name]: { ...current, ...partial } };
    });
  };

  const requiredMissing = useMemo(() => {
    return editableFields
      .filter((spec) => spec.required)
      .filter((spec) => {
        const entry = entries[spec.name];
        return !entry || !isFieldFilled(spec, entry);
      })
      .map((spec) => spec.label);
  }, [editableFields, entries]);

  const allFieldsBlank = useMemo(() => {
    if (editableFields.length === 0) return false;
    return editableFields.every((spec) => {
      const entry = entries[spec.name];
      return !entry || !isFieldFilled(spec, entry);
    });
  }, [editableFields, entries]);

  const documentId = detail.item.id;
  const parcelNumber = detail.selectedParcelNumber;

  const pushMutation = useMutation({
    mutationFn: () => {
      const values: Record<string, unknown> = {};
      for (const spec of editableFields) {
        const entry = entries[spec.name];
        if (!entry) continue;
        values[spec.name] = coerceValue(spec, entry);
      }
      if (requiresActualPicker && selectedActualNumber !== null) {
        values.actual_number = selectedActualNumber;
      }
      return pushToSymphony(documentId, { values });
    },
    onSuccess: () => {
      setPushError(null);
      void qc.invalidateQueries({ queryKey: ["document", documentId] });
      void qc.invalidateQueries({ queryKey: ["emails"] });
      void qc.invalidateQueries({ queryKey: ["email"] });
      dispatchToast(
        <Toast>
          <ToastTitle>Pushed to Symphony</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && isObject(err.body)) {
        const body = err.body as Record<string, unknown>;
        const detailField = body.detail;
        if (isObject(detailField)) {
          setPushError({
            detail: String(detailField.detail ?? "Symphony rejected the push"),
            statusCode:
              typeof detailField.statusCode === "number"
                ? detailField.statusCode
                : null,
            body: detailField.body ?? null,
          });
        } else {
          setPushError({
            detail:
              typeof detailField === "string"
                ? detailField
                : err.message,
            statusCode: err.status,
            body: err.body,
          });
        }
      } else {
        setPushError({
          detail: err instanceof Error ? err.message : "Push failed",
          statusCode: null,
          body: null,
        });
      }
    },
  });

  const isApproved = detail.item.approvalStatus === "approved";
  // Non-null payload means the doc has been pushed to Symphony at least
  // once. Treat that as the canonical "this is now a read-only audit
  // view" trigger: form fields stay visible (so the operator can verify
  // what was sent) but every control is disabled and the Push/Approve
  // affordance is hidden.
  const isPushed = detail.symphonyPayloadData != null || isApproved;
  const pushedAt = pushedAtFrom(detail.symphonyPayloadData);
  const actualMissing = requiresActualPicker && selectedActualNumber === null;
  const pushDisabled =
    isPushed ||
    parcelNumber === null ||
    editableFields.length === 0 ||
    requiredMissing.length > 0 ||
    allFieldsBlank ||
    actualMissing ||
    pushMutation.isPending;

  const tradeLabel = detail.item.tradeKey ?? "(no trade)";

  return (
    <div
      style={{
        background: tokens.colorNeutralBackground1,
        padding: 12,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: 6,
        marginBottom: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Text size={300} weight="semibold">
            Symphony update
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
            {tradeLabel}
            {" · "}
            {parcelNumber !== null
              ? `parcel ${parcelNumber}`
              : "no parcel selected"}
          </Text>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<Eye20Regular />}
            disabled={parcelNumber === null}
            onClick={() => setPreviewOpen(true)}
            title={
              parcelNumber === null
                ? "Select a parcel to preview Symphony state"
                : undefined
            }
          >
            Preview Symphony state
          </Button>
        </div>
      </div>

      {requiresActualPicker ? (
        <ActualPicker
          parcelNumber={parcelNumber}
          options={actualOptions}
          selected={selectedActualNumber}
          onChange={setSelectedActualNumber}
          isLoading={parcelQuery.isLoading}
          isError={parcelQuery.isError}
          readOnly={isPushed}
        />
      ) : null}

      {editableFields.length === 0 ? (
        <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
          No Symphony-bound fields for this document type.
        </Text>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}
        >
          {editableFields.map((spec) => (
            <SymphonyFieldRow
              key={spec.name}
              spec={spec}
              entry={entries[spec.name] ?? defaultEntryFor(spec)}
              onChange={(partial) => setEntry(spec.name, partial)}
              documentType={fieldSet.documentType}
              motCandidates={detail.motCandidates}
              readOnly={isPushed}
            />
          ))}
        </div>
      )}

      {pushError !== null ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              Symphony rejected the push
              {pushError.statusCode ? ` (${pushError.statusCode})` : ""}
            </MessageBarTitle>
            <Text>{pushError.detail}</Text>
            {pushError.body !== null && pushError.body !== undefined ? (
              <Accordion collapsible>
                <AccordionItem value="body">
                  <AccordionHeader>Error details</AccordionHeader>
                  <AccordionPanel>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 12,
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(pushError.body, null, 2)}
                    </pre>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            ) : null}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      {isPushed ? (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Pushed to Symphony</MessageBarTitle>
            <Text>
              {pushedAt
                ? `Submitted on ${pushedAt}. The fields above show what was sent — read-only.`
                : "The fields above show what was sent — read-only."}
            </Text>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {!isPushed && parcelNumber === null ? (
        <Badge appearance="tint" color="warning">
          Select a parcel before pushing
        </Badge>
      ) : null}
      {!isPushed && requiredMissing.length > 0 && parcelNumber !== null ? (
        <Badge appearance="tint" color="warning">
          Required fields missing: {requiredMissing.join(", ")}
        </Badge>
      ) : null}
      {!isPushed &&
      allFieldsBlank &&
      requiredMissing.length === 0 &&
      parcelNumber !== null ? (
        <Badge appearance="tint" color="warning">
          Fill at least one field to push.
        </Badge>
      ) : null}
      {!isPushed && actualMissing && parcelNumber !== null ? (
        <Badge appearance="tint" color="warning">
          Pick the actual to update before pushing.
        </Badge>
      ) : null}

      {!isPushed ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            appearance="primary"
            icon={
              pushMutation.isPending ? <Spinner size="tiny" /> : <ArrowUpload20Regular />
            }
            disabled={pushDisabled}
            onClick={() => pushMutation.mutate()}
          >
            {pushMutation.isPending ? "Pushing…" : "Push to Symphony"}
          </Button>
        </div>
      ) : null}

      {previewOpen && parcelNumber !== null ? (
        <ParcelPreviewModal
          parcelNumber={parcelNumber}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </div>
  );
}

interface ActualOption {
  actualNumber: number;
  label: string;
}

function extractActualOptions(parcel: Record<string, unknown>): ActualOption[] {
  // Symphony nests the per-actual list under ``allocationItem.actuals`` for
  // GET parcel responses; the create_actual response puts it at the top
  // level. Check both so the picker works in either shape.
  const allocItem = parcel.allocationItem;
  const nested = isObject(allocItem) ? allocItem.actuals : null;
  const raw =
    (Array.isArray(nested) ? nested : null) ??
    (Array.isArray(parcel.actuals) ? (parcel.actuals as unknown[]) : []);
  const out: ActualOption[] = [];
  for (const a of raw) {
    if (!isObject(a)) continue;
    const num = a.aiEstActualNum;
    // Symphony seeds a placeholder actual with ``aiEstActualNum=0`` on the
    // allocation item — it has no quantities and isn't PATCHable. Skip it.
    if (typeof num !== "number" || num <= 0) continue;
    const dt = typeof a.actualDateTime === "string" ? a.actualDateTime : null;
    const gross =
      typeof a.actualGrossQuantity === "number" ? a.actualGrossQuantity : null;
    const uom =
      typeof a.actualGrossQtyUom === "string" ? a.actualGrossQtyUom : null;
    const ticket = typeof a.ticketNum === "string" ? a.ticketNum : null;
    const parts: string[] = [`#${num}`];
    if (dt) {
      const parsed = Date.parse(dt);
      parts.push(Number.isNaN(parsed) ? dt : DATETIME_FMT.format(new Date(parsed)));
    }
    if (gross !== null && uom) parts.push(`${gross} ${uom}`);
    else if (gross !== null) parts.push(String(gross));
    if (ticket) parts.push(`ticket ${ticket}`);
    out.push({ actualNumber: num, label: parts.join(" · ") });
  }
  return out;
}

interface ActualPickerProps {
  parcelNumber: number | null;
  options: ActualOption[];
  selected: number | null;
  onChange: (n: number | null) => void;
  isLoading: boolean;
  isError: boolean;
  readOnly?: boolean;
}

function ActualPicker({
  parcelNumber,
  options,
  selected,
  onChange,
  isLoading,
  isError,
  readOnly = false,
}: ActualPickerProps) {
  if (parcelNumber === null) {
    return (
      <Field
        label="Actual to update *"
        hint={
          <HelperText>
            Select a parcel above; the certificate PATCHes one of its actuals.
          </HelperText>
        }
      >
        <Dropdown disabled placeholder="Select a parcel first" />
      </Field>
    );
  }
  if (isLoading) {
    return (
      <Field label="Actual to update *">
        <Dropdown disabled placeholder="Loading actuals from Symphony…" />
      </Field>
    );
  }
  if (isError) {
    return (
      <Field
        label="Actual to update *"
        validationState="error"
        validationMessage="Failed to load parcel actuals from Symphony"
      >
        <Dropdown disabled placeholder="—" />
      </Field>
    );
  }
  if (options.length === 0) {
    return (
      <Field
        label="Actual to update *"
        validationState="error"
        validationMessage="No actuals on this parcel — push the Bill of Lading first."
      >
        <Dropdown disabled placeholder="—" />
      </Field>
    );
  }

  const selectedOption = options.find((o) => o.actualNumber === selected);
  return (
    <Field
      label="Actual to update *"
      hint={
        options.length === 1 ? (
          <HelperText>One actual on the parcel — auto-selected.</HelperText>
        ) : undefined
      }
    >
      <Dropdown
        value={selectedOption ? selectedOption.label : ""}
        selectedOptions={selected !== null ? [String(selected)] : []}
        disabled={readOnly}
        onOptionSelect={(_, data) => {
          const next = data.optionValue ? Number(data.optionValue) : null;
          onChange(Number.isFinite(next) ? next : null);
        }}
        placeholder="Pick an actual"
      >
        {options.map((o) => (
          <Option key={o.actualNumber} value={String(o.actualNumber)}>
            {o.label}
          </Option>
        ))}
      </Dropdown>
    </Field>
  );
}

interface SymphonyFieldRowProps {
  spec: FieldSpec;
  entry: FieldEntry;
  onChange: (partial: Partial<FieldEntry>) => void;
  documentType: string;
  motCandidates: MotCandidate[];
  // True when the doc has been pushed to Symphony (``symphony_payload_data``
  // is non-null). Renders the row in display-only mode: inputs visible
  // but disabled so the operator can verify what was sent without
  // being able to edit it.
  readOnly?: boolean;
}

function FieldLabel({ spec }: { spec: FieldSpec }) {
  if (spec.required) {
    return <>{`${spec.label} *`}</>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {spec.label}
      <Badge appearance="ghost" size="small" color="informative">
        Optional
      </Badge>
    </span>
  );
}

function SymphonyFieldRow({
  spec,
  entry,
  onChange,
  documentType,
  motCandidates,
  readOnly = false,
}: SymphonyFieldRowProps) {
  const label = <FieldLabel spec={spec} />;
  const filled = isFieldFilled(spec, entry);
  const showOptionalSkipHint = !spec.required && !filled;
  const inputAppearance: "outline" | "filled-darker" =
    showOptionalSkipHint ? "filled-darker" : "outline";
  // BL/QC use this to surface the parcel's primary/secondary UOM
  // ("Parcel expects BBL"). Composed alongside per-branch hints below.
  const specHint = spec.hint ? <HelperText>{spec.hint}</HelperText> : null;

  // Special-case: fixture_recap carrier with Fabric candidates renders a
  // Combobox so the operator can type a vessel name and pick from the
  // top fuzzy matches refreshed from Fabric on every (debounced) keystroke.
  // When Fabric was unavailable at detail time the backend sends
  // kind="value", type="string" — fall through to the standard text input.
  if (
    documentType === "fixture_recap" &&
    spec.name === "carrier" &&
    spec.kind === "choice"
  ) {
    return (
      <CarrierComboboxField
        entry={entry}
        onChange={onChange}
        initialCandidates={motCandidates}
        label={label}
        appearance={inputAppearance}
        readOnly={readOnly}
      />
    );
  }

  if (spec.kind === "choice") {
    const choices = spec.choices ?? [];
    const inManualMode = entry.choiceState === "manual";

    if (choices.length === 0) {
      const fallbackText = spec.required
        ? "No candidates extracted — enter manually."
        : "No candidates extracted. Leave empty to skip this field on push.";
      return (
        <Field
          label={label}
          hint={
            <>
              {specHint}
              <HelperText>{fallbackText}</HelperText>
            </>
          }
        >
          <ManualValueInput
            spec={spec}
            value={entry.value}
            onChange={(value) => onChange({ value, choiceState: "manual" })}
            appearance={inputAppearance}
            readOnly={readOnly}
          />
        </Field>
      );
    }

    if (inManualMode) {
      return (
        <Field
          label={label}
          hint={
            <>
              {specHint}
              <Link
                as="button"
                type="button"
                onClick={() => {
                  const fallbackIdx = 0;
                  const raw = choices[fallbackIdx]?.value;
                  onChange({
                    choiceState: fallbackIdx,
                    value:
                      spec.type === "datetime"
                        ? toDatetimeLocalInputString(raw)
                        : valueToInputString(raw),
                  });
                }}
              >
                Use options
              </Link>
            </>
          }
        >
          <ManualValueInput
            spec={spec}
            value={entry.value}
            onChange={(value) => onChange({ value })}
            appearance={inputAppearance}
            readOnly={readOnly}
          />
        </Field>
      );
    }

    const selectedIdx =
      typeof entry.choiceState === "number" ? entry.choiceState : -1;
    const selectedKeys = selectedIdx >= 0 ? [String(selectedIdx)] : [];
    const selectedRaw = selectedIdx >= 0 ? choices[selectedIdx]?.label : undefined;
    const selectedLabel =
      selectedRaw !== undefined ? formatChoiceLabel(spec, selectedRaw) : undefined;
    const skipHint = showOptionalSkipHint ? (
      <HelperText>Leave empty to skip this field on push.</HelperText>
    ) : null;
    const dropdownHint =
      specHint || skipHint ? (
        <>
          {specHint}
          {skipHint}
        </>
      ) : undefined;

    return (
      <Field label={label} hint={dropdownHint}>
        <Dropdown
          appearance={inputAppearance}
          selectedOptions={selectedKeys}
          value={selectedLabel ?? ""}
          disabled={readOnly}
          onOptionSelect={(_, data) => {
            const key = data.optionValue;
            if (key === undefined) return;
            if (key === CUSTOM_OPTION_KEY) {
              onChange({
                choiceState: "manual",
                value: spec.type === "datetime" ? "" : entry.value,
              });
              return;
            }
            const idx = Number(key);
            const choice = choices[idx];
            onChange({
              choiceState: idx,
              value:
                spec.type === "datetime"
                  ? toDatetimeLocalInputString(choice?.value)
                  : valueToInputString(choice?.value),
            });
          }}
        >
          {choices.map((choice, idx) => {
            const display = formatChoiceLabel(spec, choice.label);
            const subtitle = choiceSubtitle(spec, choice.label);
            return (
              <Option key={idx} value={String(idx)} text={display}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Text>{display}</Text>
                  {subtitle ? (
                    <Text
                      size={200}
                      style={{ color: tokens.colorNeutralForeground2 }}
                    >
                      {subtitle}
                    </Text>
                  ) : null}
                </div>
              </Option>
            );
          })}
          <Option value={CUSTOM_OPTION_KEY} text="Custom value…">
            <Text style={{ fontStyle: "italic" }}>Custom value…</Text>
          </Option>
        </Dropdown>
      </Field>
    );
  }

  // kind === "value"
  if (spec.type === "boolean") {
    const checked = entry.value === "true";
    return (
      <Field label={label}>
        <Dropdown
          selectedOptions={[String(checked)]}
          value={checked ? "Yes" : "No"}
          disabled={readOnly}
          onOptionSelect={(_, data) => {
            if (data.optionValue !== undefined) {
              onChange({ value: data.optionValue });
            }
          }}
        >
          <Option value="true" text="Yes">
            Yes
          </Option>
          <Option value="false" text="No">
            No
          </Option>
        </Dropdown>
      </Field>
    );
  }
  const skipHint = showOptionalSkipHint ? (
    <HelperText>Leave empty to skip this field on push.</HelperText>
  ) : null;
  const valueHint =
    specHint || skipHint ? (
      <>
        {specHint}
        {skipHint}
      </>
    ) : undefined;
  return (
    <Field label={label} hint={valueHint}>
      <ManualValueInput
        spec={spec}
        value={entry.value}
        onChange={(value) => onChange({ value })}
        appearance={inputAppearance}
        readOnly={readOnly}
      />
    </Field>
  );
}

/** Pull ``pushed_at`` (ISO 8601) out of a stored Symphony payload and
 * format it as a locale-friendly string. The wrapper shape is set by
 * the backend in ``routes/documents.py.symphony_push`` —
 * ``{document_type, pushed_at, fields}``. Returns ``null`` for any
 * unexpected shape so the banner just falls back to "Pushed to
 * Symphony" without a date. */
function pushedAtFrom(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = (payload as { pushed_at?: unknown }).pushed_at;
  if (typeof raw !== "string") return null;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleString();
}

function HelperText({ children }: { children: ReactNode }) {
  return (
    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
      {children}
    </Text>
  );
}

interface ManualValueInputProps {
  spec: FieldSpec;
  value: string;
  onChange: (value: string) => void;
  appearance?: "outline" | "filled-darker";
  readOnly?: boolean;
}

function ManualValueInput({
  spec,
  value,
  onChange,
  appearance = "outline",
  readOnly = false,
}: ManualValueInputProps) {
  if (spec.type === "number") {
    return (
      <NumericManualInput
        value={value}
        onChange={onChange}
        appearance={appearance}
        readOnly={readOnly}
      />
    );
  }
  return (
    <Input
      appearance={appearance}
      type={inputTypeFor(spec)}
      value={value}
      disabled={readOnly}
      onChange={(_, d) => onChange(d.value)}
      contentBefore={
        spec.type === "datetime" ? (
          <Clock20Regular aria-hidden="true" />
        ) : undefined
      }
    />
  );
}

/**
 * Numeric manual input: shows locale thousands separators while idle,
 * raw digits while focused so editing isn't fighting commas. The stored
 * form value stays comma-free; ``coerceValue`` also strips commas
 * defensively before parsing.
 */
function NumericManualInput({
  value,
  onChange,
  appearance,
  readOnly,
}: {
  value: string;
  onChange: (next: string) => void;
  appearance: "outline" | "filled-darker";
  readOnly: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const raw = value.replace(/,/g, "");
  const display =
    focused || parseNumeric(raw) === null ? raw : formatWithThousands(raw);
  return (
    <Input
      appearance={appearance}
      type="text"
      inputMode="decimal"
      value={display}
      disabled={readOnly}
      onChange={(_, d) => onChange(d.value.replace(/,/g, ""))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

const useCarrierComboboxStyles = makeStyles({
  // Cap the popup height so a long candidate list scrolls inside the
  // dropdown rather than running off the viewport. Inline ``style`` on
  // the listbox slot loses to Fluent's default class for this property,
  // so we go through ``makeStyles`` to get a class that wins by source
  // order. ``maxHeight: 320px`` ≈ 4 rich-option rows (~80px each).
  listbox: {
    maxHeight: "320px",
    overflowY: "auto",
  },
});

interface CarrierComboboxFieldProps {
  entry: FieldEntry;
  onChange: (partial: Partial<FieldEntry>) => void;
  initialCandidates: MotCandidate[];
  label: JSX.Element;
  appearance: "outline" | "filled-darker";
  readOnly?: boolean;
}

function motOptionLabel(c: MotCandidate): string {
  return `${c.motFullName} (${c.motCode})`;
}

/**
 * Carrier Combobox for fixture_recap pushes.
 *
 * The option list starts from the FieldSpec's MOT candidates (Fabric
 * fuzzy match on the LLM-extracted vessel name). The operator can type
 * a different vessel name and the list re-queries Fabric (debounced 300ms
 * via useDebouncedValue). The committed form value is the picked motCode;
 * if the operator types freeform text and doesn't pick anything, that
 * text is pushed as-is (Combobox freeform mode).
 */
const CREATE_VESSEL_SENTINEL = "__create_vessel__";

function CarrierComboboxField({
  entry,
  onChange,
  initialCandidates,
  label,
  appearance,
  readOnly = false,
}: CarrierComboboxFieldProps) {
  const styles = useCarrierComboboxStyles();
  const initialLabel = useMemo(() => {
    const match = initialCandidates.find((c) => c.motCode === entry.value);
    return match ? motOptionLabel(match) : entry.value;
  }, [initialCandidates, entry.value]);

  const [query, setQuery] = useState<string>(initialLabel);
  const [options, setOptions] = useState<MotCandidate[]>(initialCandidates);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Tracks the label last written by an option-selection event. The
  // refetch effect compares against this ref to skip the round trip when
  // ``debouncedQuery`` is just an echo of the operator's pick (avoids
  // hitting Fabric every time they select something). The onInput handler
  // clears it so subsequent free-text edits trigger a refetch.
  const lastSelectedLabelRef = useRef<string | null>(null);

  // Debounce the typed query so we only hit Fabric ~300ms after the
  // operator stops typing. Empty / whitespace queries short-circuit to
  // the initial candidates (no network call).
  const debouncedQuery = useDebouncedValue(query, 300);

  useEffect(() => {
    if (debouncedQuery === lastSelectedLabelRef.current) {
      return;
    }
    const trimmed = debouncedQuery.trim();
    if (trimmed === "") {
      setOptions(initialCandidates);
      setIsLoading(false);
      return;
    }
    const controller = new AbortController();
    setIsLoading(true);
    lookupMotCandidates(trimmed, undefined, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setOptions(next);
      })
      .catch(() => {
        // Fabric outage is non-fatal; keep showing the prior options.
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [debouncedQuery, initialCandidates]);

  const selectedKeys = useMemo(() => {
    const has = options.some((c) => c.motCode === entry.value);
    return has ? [entry.value] : [];
  }, [options, entry.value]);

  const handleVesselCreated = useCallback(
    (motCode: string, motFullName: string, imoNum: string | null) => {
      const synthetic: MotCandidate = { motCode, motFullName, imoNum, score: 100 };
      setOptions((prev) => [synthetic, ...prev]);
      const newLabel = motOptionLabel(synthetic);
      lastSelectedLabelRef.current = newLabel;
      setQuery(newLabel);
      onChange({ value: motCode, choiceState: "manual" });
      setIsCreateModalOpen(false);
    },
    [onChange],
  );

  return (
    <>
      <Field label={label}>
        <Combobox
          appearance={appearance}
          freeform
          placeholder="Type a vessel name…"
          value={query}
          selectedOptions={selectedKeys}
          disabled={readOnly}
          // Constrain the popup to the trigger's width so long candidate
          // labels (e.g. "SOMTRANS XXXIII (02334844)") don't push the
          // listbox out past the form field. ``matchTargetSize: "width"``
          // is Fluent's hook for this. ``maxHeight`` caps long result
          // lists so the dropdown scrolls instead of overflowing the
          // viewport.
          positioning={{ matchTargetSize: "width" }}
          listbox={{ className: styles.listbox }}
          onInput={(ev) => {
            // Typing is a SEARCH action only — it refreshes the dropdown
            // but never commits an MOT code. The operator must pick from
            // the dropdown to set ``entry.value`` (an actual mot_code).
            // This keeps invalid free-text carriers out of the Symphony push.
            const next = (ev.target as HTMLInputElement).value;
            lastSelectedLabelRef.current = null;
            setQuery(next);
          }}
          onOptionSelect={(_, data) => {
            const code = data.optionValue;
            if (code === undefined) return;
            if (code === CREATE_VESSEL_SENTINEL) {
              setIsCreateModalOpen(true);
              return;
            }
            const pick = options.find((c) => c.motCode === code);
            if (!pick) return;
            const newLabel = motOptionLabel(pick);
            lastSelectedLabelRef.current = newLabel;
            setQuery(newLabel);
            onChange({ value: pick.motCode, choiceState: "manual" });
          }}
        >
          {isLoading && options.length === 0 ? (
            <Option key="__loading__" value="" disabled text="Loading…">
              <Text style={{ fontStyle: "italic" }}>Loading…</Text>
            </Option>
          ) : null}
          {options.length === 0 && !isLoading ? (
            <Option key="__empty__" value="" disabled text="No matches">
              <Text style={{ fontStyle: "italic" }}>
                No matches — type a vessel name to search Fabric.
              </Text>
            </Option>
          ) : null}
          {options.map((c) => {
            const display = motOptionLabel(c);
            return (
              <Option key={c.motCode} value={c.motCode} text={display}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    // Constrain inside the listbox; long labels truncate
                    // rather than widening the popup. ``minWidth: 0`` is
                    // required for ``text-overflow: ellipsis`` to apply
                    // inside a flex child.
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                  title={display}
                >
                  <Text
                    truncate
                    block
                    wrap={false}
                    style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {display}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground2 }}>
                    score {c.score.toFixed(1)}
                  </Text>
                </div>
              </Option>
            );
          })}
          <Option
            key={CREATE_VESSEL_SENTINEL}
            value={CREATE_VESSEL_SENTINEL}
            text="Create new vessel"
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <AddCircle20Regular />
              <Text>Create new vessel</Text>
            </div>
          </Option>
        </Combobox>
      </Field>
      <CreateVesselModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onVesselCreated={handleVesselCreated}
      />
    </>
  );
}

interface CreateVesselModalProps {
  open: boolean;
  onClose: () => void;
  onVesselCreated: (motCode: string, motFullName: string, imoNum: string | null) => void;
}

function CreateVesselModal({ open, onClose, onVesselCreated }: CreateVesselModalProps) {
  const [imo, setImo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successNoVessel, setSuccessNoVessel] = useState(false);

  // Reset state when the modal opens so a second use starts fresh.
  useEffect(() => {
    if (open) {
      setImo("");
      setError(null);
      setSuccessNoVessel(false);
    }
  }, [open]);

  async function handleSubmit() {
    const trimmed = imo.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) {
      setError("Please enter a valid numeric IMO number.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createVessel(parseInt(trimmed, 10));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Vessel creation failed.");
      setIsSubmitting(false);
      return;
    }

    // Vessel created — try to find it in Fabric by IMO for pre-selection.
    try {
      const candidates = await lookupMotCandidates(trimmed);
      const vessel = candidates[0];
      if (vessel !== undefined) {
        onVesselCreated(vessel.motCode, vessel.motFullName, vessel.imoNum);
        return;
      }
    } catch {
      // Fabric lookup failure after creation is non-fatal.
    }

    // Vessel created but not yet visible in Fabric (sync delay).
    setSuccessNoVessel(true);
    setIsSubmitting(false);
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: 400 }}>
        <DialogBody>
          <DialogTitle>Create new vessel</DialogTitle>
          <DialogContent>
            {successNoVessel ? (
              <MessageBar intent="success">
                <MessageBarBody>
                  <MessageBarTitle>Vessel created</MessageBarTitle>
                  It may take a moment to appear — search by IMO to pick it once Fabric
                  synchronises.
                </MessageBarBody>
              </MessageBar>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field
                  label="IMO number"
                  validationState={error ? "error" : "none"}
                  validationMessage={error ?? undefined}
                >
                  <Input
                    inputMode="numeric"
                    placeholder="e.g. 9123456"
                    value={imo}
                    disabled={isSubmitting}
                    onChange={(_, d) => {
                      setError(null);
                      setImo(d.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void handleSubmit();
                    }}
                  />
                </Field>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {successNoVessel ? (
              <Button appearance="primary" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => void handleSubmit()}
                  disabled={isSubmitting || imo.trim() === ""}
                  icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
                >
                  {isSubmitting ? "Creating…" : "Create"}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
